import type { ExpectedRow, VisitorPass } from '../interfaces/visitor-pass';

/**
 * THE WALK-IN MATCH (resident-portal Plan 3, RP-3.9).
 *
 * A concierge typing a visitor into the ordinary wizard may be typing someone
 * who already has a pass for that unit. Names are encrypted with no blind index
 * (F-9, on purpose), so the match is made HERE, in the browser, against the
 * passes the desk may already read for that one unit — never a server search.
 *
 * NORMALISED, NOT FUZZY. NFKD, combining marks stripped, lower-cased, runs of
 * white space collapsed: "Lópéz", "lopez" and " LOPEZ " are one name; "Lopes"
 * is not. A near-miss must not produce a banner that invites a one-tap sign-in
 * for the wrong person (R-3) — and even an exact match never picks for the
 * desk: every match is listed and a human confirms.
 *
 * A MATCH IS first AND last equal, or first equal when the pass has no last
 * name. A guest typed as one string ("maria lopez" in the first-name box, the
 * last-name box empty) is split on its first space — that is how a hurried desk
 * types, and it is the same person.
 */

export const normaliseName = (value: string | null | undefined): string =>
  String(value ?? '')
    .normalize('NFKD')
    .replace(/\p{M}+/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

export interface GuestName {
  first: string;
  last: string;
}

/** The wizard's index-aligned guest arrays as normalised {first, last} pairs;
 *  blank rows dropped; "maria lopez" typed into first alone is split. */
export const guestNames = (
  names: readonly string[] | null | undefined,
  lastNames: readonly string[] | null | undefined
): GuestName[] =>
  (names || [])
    .map((raw, i) => {
      let first = normaliseName(raw);
      let last = normaliseName((lastNames || [])[i]);
      if (!last && first.includes(' ')) {
        const at = first.indexOf(' ');
        last = first.slice(at + 1);
        first = first.slice(0, at);
      }

      return { first, last };
    })
    .filter(g => g.first.length > 0);

export const passMatchesGuest = (
  pass: Pick<VisitorPass, 'first_name' | 'last_name'>,
  guest: GuestName
): boolean => {
  const first = normaliseName(pass.first_name);
  const last = normaliseName(pass.last_name);
  if (!first || first !== guest.first) return false;

  return last ? last === guest.last : true;
};

/** Every pass any guest matches, in the order the list gave them, each once. */
export const matchPasses = <P extends Pick<VisitorPass, 'pass_id' | 'first_name' | 'last_name'>>(
  passes: readonly P[],
  guests: readonly GuestName[]
): P[] => passes.filter(pass => guests.some(guest => passMatchesGuest(pass, guest)));

/**
 * SIGNABLE NOW — the Expected screen's own rule: an occurrence of the current
 * desk day with a window state (the server said it can be signed in now) and
 * room left in the party. Anything else is "has a pass, but not now".
 */
export const signableRowFor = (
  passId: string,
  rows: readonly ExpectedRow[] | null | undefined
): ExpectedRow | null =>
  // The same predicate as expected-model.ts isSignable, restated here so this
  // util does not reach into a page folder.
  (rows || []).find(
    row => row.pass_id === passId && row.window_state !== null && row.party_remaining > 0
  ) || null;

/** Where the banner's action lands: the Expected screen with that row's
 *  confirm open. `from=walk-in` tells it to clear the wizard draft once the
 *  sign-in succeeds (the visitor is signed in; the half-typed entry is moot). */
export const expectedHrefFor = (passId: string): string =>
  `/visitor-log/expected?pass=${encodeURIComponent(passId)}&from=walk-in`;
